import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPinIcon, Mail, XCircle, Link } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { useBookingsStore } from '@/lib/stores/bookings-store';
import { useNavigate } from 'react-router-dom';
import { getBookings, type BookingFilter } from '@/lib/utils/bookingFilters';
import { getFirstImageUrl, getCurrencySymbol, getGoogleMapsUrl } from '@/lib/utils/bookingHelpers';
import { GuestManagement } from '@/components/profile/GuestManagement';

export function Bookings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bookings, fetchUserBookings } = useBookingsStore();
  const [filter, setFilter] = React.useState<BookingFilter>('all');
  const hasFetchedRef = React.useRef(false);

  useEffect(() => {
    if (user?.id && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchUserBookings(user.id);
    }
  }, [user?.id, fetchUserBookings]);

  // Reset fetch flag when user changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [user?.id]);

  if (!user) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-sand-400">Please sign in to view your bookings</p>
      </div>
    );
  }

  // Use single flexible getBookings function
  const filteredBookings = (() => {
    switch (filter) {
      case 'upcoming':
        return getBookings(bookings, { status: 'confirmed', dateFilter: 'upcoming' });
      case 'past':
        return getBookings(bookings, { status: 'confirmed', dateFilter: 'past' });
      case 'cancelled':
        return getBookings(bookings, { status: 'cancelled' });
      case 'all':
      default:
        return getBookings(bookings, { status: ['confirmed', 'cancelled'] });
    }
  })();

  return (
    <div className="min-h-screen pt-20 pb-12 bg-earth-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/profile')}
                className="mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Profile
              </Button>
              <h1 className="text-3xl font-display">My Bookings</h1>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={filter === 'all' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
              <Button
                variant={filter === 'upcoming' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('upcoming')}
              >
                Upcoming
              </Button>
              <Button
                variant={filter === 'past' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('past')}
              >
                Past
              </Button>
              <Button
                variant={filter === 'cancelled' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('cancelled')}
              >
                Cancelled
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {filteredBookings.map((booking, index) => {
              const experience = booking.experience;
                            
              if (!experience) return null;

              return (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-earth-800/50 rounded-lg overflow-hidden"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="h-60 md:w-2/5 flex-shrink-0">
                      <img 
                        src={getFirstImageUrl(experience)} 
                        alt={experience.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                    <div className="p-6 md:w-3/5">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-sand-100 mb-2">{experience.title}</h3>
                          <p className="text-sand-400 line-clamp-2">{experience.short_description || experience.description}</p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm ${
                          booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                          booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </div>
                      </div>

                      <div className="mb-6 space-y-2">
                        {experience.location && (
                          <div className="flex items-center gap-2 text-sand-300">
                            <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                            <span>{experience.location.city || experience.location.name}</span>
                            {experience.location.address && (
                              <a 
                                href={getGoogleMapsUrl(experience.location)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sand-100 underline ml-2 flex items-center gap-1"
                              >
                                <Link className="h-3 w-3" />
                                <span>Open in Maps</span>
                              </a>
                            )}
                          </div>
                        )}
                        
                        {experience.creator?.creator_profile?.business_name && (
                          <div className="flex items-center gap-2 text-sand-300">
                            <Mail className="h-4 w-4 flex-shrink-0" />
                            <span>Contact Creator:</span>
                            <a 
                              href={`mailto:${experience.creator?.creator_profile?.contact_email || 'info@letsrendezvous.co'}`}
                              className="text-sand-100 underline"
                            >
                              {experience.creator?.creator_profile?.business_name || 'Experience Creator'}
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-sand-400">Total Price</p>
                          <p className="text-xl font-semibold text-sand-100">
                            {getCurrencySymbol(experience.pricing_config?.currency)}{booking.total_amount}
                          </p>  
                        </div>
                        {booking.status === 'pending' && (
                          <Button
                            variant="outline"
                            className="text-red-500 hover:text-red-400"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to cancel this booking?')) {
                                useBookingsStore.getState().cancelBooking(booking.id);
                              }
                            }}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Cancel Booking
                          </Button>
                        )}
                      </div>

                      {booking.status === 'confirmed' && (
                        <div className="mt-4 flex items-start gap-2 p-3 bg-sand-400/10 rounded-lg">
                          <p className="text-sm text-sand-300">
                            Your booking is confirmed!
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Guest Management - only show for confirmed bookings with multiple participants */}
                  {booking.status === 'confirmed' && (booking.participant_count ?? 0) > 1 && (
                    <div className="px-6 pb-6">
                      <GuestManagement 
                        booking={booking} 
                        onGuestsUpdated={() => fetchUserBookings(user.id)}
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}

            {filteredBookings.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sand-400 mb-4">No bookings found</p>
                <Button onClick={() => navigate('/experiences')}>
                  Explore Experiences
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}